import { Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { isEmpty, matches } from '@sama/micro-dash';
import { MessagingService } from '../../cross-team-shared/messaging/messaging.service';
import { isCheckboxOutput } from '../../cross-team-shared/misc-utils/output-utils';
import { ObjectWith } from '../../cross-team-shared/types/misc';
import { isAnnotationOutput } from './dsl/sensor-fusion/sensor-fusion-output.utils';
import { isBlank } from './state/output-info';
import { TaskWorkspaceStore } from './state/task-workspace-store';

@Injectable()
export class H3mlHelper {
  private warned: ObjectWith<boolean> = {};

  constructor(
    private messaging: MessagingService,
    private store: TaskWorkspaceStore,
  ) {}

  install(component: any) {
    component.input = this.input.bind(this);
    component.output = this.output.bind(this);
    component.regexMatch = this.regexMatch.bind(this);
    component.isBlank = this.isBlank.bind(this);
    component.isChecked = this.isChecked.bind(this);
  }

  // eslint-disable-next-line complexity -- if you need to modify this code, fix this first
  input(name: string) {
    const state = this.store.state();
    const projectInput = state.project.inputs.find(matches({ name }));
    if (!projectInput) {
      this.warnOfBadName(
        `The layout contains an invalid input name within input(): ${name}`,
      );
      return;
    }

    let inputValue = state.task.data[name];

    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!state.task || !state.task.data || !inputValue) {
      return;
    }

    if (projectInput.format === 'URL') {
      inputValue = this.store.getInputUrl(name);
    }

    return inputValue;
  }

  output(name: string) {
    const state = this.store.state();
    if (!state.project.outputs.find(matches({ name }))) {
      this.warnOfBadName(
        `The layout contains an invalid output name within output(): ${name}`,
      );
    }
    return this.store.extractAnswersFromOutputInfo()[name];
  }

  regexMatch(value: string, regex: string) {
    return !!value && new RegExp(regex).test(value);
  }

  isBlank(outputName: string) {
    const info = this.store.state().outputInfoByName[outputName];
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (info) {
      if (this.store.isUsingObjects() && isAnnotationOutput(info.output)) {
        const annotationInfo =
          this.store.state().outputAnnotationInfo[outputName];
        assert(
          annotationInfo,
          'Objects enabled projects have annotationInfo for all annotation outputs',
        );
        return isEmpty(annotationInfo.shapes);
      }
      return isBlank(info);
    } else {
      this.warnOfBadName(
        `The layout contains an invalid output name within isBlank(): ${outputName}`,
      );
      return true;
    }
  }

  // eslint-disable-next-line complexity -- if you need to modify this code, fix this first
  isChecked(outputName: string, answerOptionName: string) {
    const info = this.store.state().outputInfoByName[outputName];
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!info) {
      this.warnOfBadName(
        `The layout contains an invalid output name within isChecked(): ${outputName}`,
      );
      return false;
    }

    if (!isCheckboxOutput(info.output)) {
      this.warnOfBadName(
        `The layout uses isChecked() for a non-checkbox output: ${outputName}`,
      );
      return false;
    }

    const options = info.output.answer_options;
    if (!options.find((o) => o.name === answerOptionName)) {
      this.warnOfBadName(
        `The layout contains an invalid answer option value within isChecked(): ${answerOptionName}`,
      );
      return false;
    }

    if (!info.isVisible) {
      return false;
    }

    return info.answer && info.answer[answerOptionName] === '1';
  }

  private warnOfBadName(warning: string) {
    if (!this.warned[warning]) {
      this.warned[warning] = true;
      this.messaging.error(warning);
    }
  }
}
